import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ResourcesItems from './ResourcesItems';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Icon } from 'legacy-packages/ft-cb';
import { isEqual } from 'lodash';

const { oneOfType, arrayOf, shape, string, any, array, bool } = PropTypes;

/**
 * Draws resources table
 **/
class ResourcesTable extends Component {
  static displayName = 'ResourcesTable';

  static propTypes = {
    /**
     * Table th column info
     * */
    columns: oneOfType([
      arrayOf(
        shape({
          /**
           * Column label
           * */
          label: string,
          /**
           * Column className
           * */
          className: string,
          /**
           * Children
           * */
          children: any,
        }),
      ),
      array,
    ]),
    /**
     * Array of items for table body
     * */
    items: array,
    /**
     * User data bool
     * */
    isStudent: bool,
    /**
     * Student context
     * */
    studentContext: shape({
      hasContext: bool,
      studentId: string,
    }),
  };

  static Sorting = new Map([
    ['Disabled', 'chevron_grouped_active'],
    ['Descending', 'chevron_single_descending'],
    ['Ascending', 'chevron_single_ascending'],
    ['DescendingSecondary', 'chevron_grouped_descending'],
    ['AscendingSecondary', 'chevron_grouped_ascending'],
  ]);

  static defaultProps = {
    items: [],
    columns: [],
    isStudent: false,
    studentContext: {
      hasContext: false,
      studentId: null,
    },
  };

  constructor(props) {
    super(props);
    const { columns } = this.props;
    let initialColumns = columns.map((col) => ({
      column: col.value,
      direction: 'Disabled',
    }));

    initialColumns[0].direction = 'Ascending';

    this.state = {
      items: props.items,
      selectedColumn: columns[0].label,
      sorting: initialColumns,
    };

    this.sortByAlpha = this.sortByAlpha.bind(this);
    this.sortByScore = this.sortByScore.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.items, this.props.items)) {
      const { items, columns } = this.props;
      if (columns.filter((col) => col.value === 'Topic').length === 1) {
        const sortedByTopic = this.sortByTopic(items);
        this.updateColumn('Topic', sortedByTopic, 0, 'Ascending');
      } else {
        const sortKey = columns[0].value;
        const sortedItems = this.sortByAlpha(items, sortKey);
        this.updateColumn(sortKey, sortedItems, 0, 'Ascending');
      }
    }
  }

  componentDidMount() {
    const { items, columns } = this.props;
    if (columns.filter((col) => col.value === 'Topic').length === 1) {
      const sortedByTopic = this.sortByTopic(items);
      this.updateColumn('Topic', sortedByTopic, 0, 'Ascending');
    } else {
      const sortKey = columns[0].value;
      const sortedItems = this.sortByAlpha(items, sortKey);
      this.updateColumn(sortKey, sortedItems, 0, 'Ascending');
    }
  }

  /**
   * Compare topics, if topics in two rows are of equivalent order,
   * sort rows ascending alphabetically by skills.
   *
   * @memberof ResourcesTable
   */
  topicCompare = (a, b, sorting = 'Ascending') => {
    const reverseSorting = sorting === 'Ascending' ? 1 : -1;

    if (a.abbreviation === b.abbreviation) {
      return 0;
    }

    if (
      !Object.prototype.hasOwnProperty.call(a, 'Topic') ||
      !Object.prototype.hasOwnProperty.call(b, 'Topic')
    ) {
      return 0;
    }

    let topicA = a.Topic.label.split(':')[0];
    let topicB = b.Topic.label.split(':')[0];
    // Split the right side of 1.X so that we can run comparisons such as 1.16 vs 1.9 vs 2.10
    topicA =
      parseInt(topicA.split('.')[0], 10) * 100 +
      parseInt(topicA.split('.')[1], 10);
    topicB =
      parseInt(topicB.split('.')[0], 10) * 100 +
      parseInt(topicB.split('.')[1], 10);
    if (topicA > topicB) {
      return 1 * reverseSorting;
    }
    if (topicA < topicB) {
      return -1 * reverseSorting;
    }

    const { columns } = this.props;
    // Sort by next column if we have two matching Topics
    let nextColumn = -1;
    columns.forEach((col, index) => {
      if (col.value === 'Topic') {
        nextColumn = index + 1;
      }
    });
    if (nextColumn > 0) {
      const key = columns[nextColumn].value;
      return this.genericCompare(a, b, key);
    }
    return 0;
  };

  /**
   * To avoid mis-comparing 1.9 with 1.16, we must convert to int
   *
   * @memberof ResourcesTable
   */
  sortByTopic(items) {
    return items.sort(this.topicCompare);
  }

  genericCompare = (a, b, objectKey) => {
    if (a.abbreviation === b.abbreviation) {
      return 0;
    }

    if (
      !Object.prototype.hasOwnProperty.call(a, objectKey) ||
      !Object.prototype.hasOwnProperty.call(b, objectKey)
    ) {
      return 0;
    }

    const labelA = a[objectKey].label;
    const labelB = b[objectKey].label;

    if (labelA > labelB) {
      return 1;
    }
    if (labelA < labelB) {
      return -1;
    }

    return 0;
  };

  /**
   * Generic sorting function
   *
   * @memberof ResourcesTable
   */
  sortByAlpha(items, selectedColumn) {
    return items.sort((a, b) => {
      if (a.abbreviation === b.abbreviation) {
        return 0;
      }
      const valueA = a[selectedColumn].label;
      const valueB = b[selectedColumn].label;

      if (valueA > valueB) {
        return 1;
      }
      if (valueA < valueB) {
        return -1;
      }
      return this.topicCompare(a, b);
    });
  }

  /**
   * It sorts by low percentage to high percent, then blanks for 'Ascending' order
   *
   *
   * @memberof ResourcesTable
   */
  sortByScore(items, column, sorting = 'Ascending') {
    const { columns } = this.props;
    const reverseSorting = sorting === 'Ascending' ? 1 : -1;
    return items.sort((a, b) => {
      const scoreA = a[columns[column].value].label;
      const scoreB = b[columns[column].value].label;

      if (scoreA === '' && scoreB !== '') {
        return 1;
      }

      if (scoreA !== '' && scoreB === '') {
        return -1;
      }

      if (scoreB === undefined && scoreA) {
        return -1;
      }

      if (scoreA === undefined && scoreB) {
        return 1;
      }

      if (
        (scoreA === '' && scoreB === '') ||
        (scoreA === undefined && scoreB === undefined)
      ) {
        //If two scores in two rows are equivalent, sort by the Topic or the first column to break ties.
        return this.topicCompare(a, b, sorting);
      }

      if (parseFloat(scoreA) > parseFloat(scoreB)) {
        return 1 * reverseSorting;
      }
      if (parseFloat(scoreA) < parseFloat(scoreB)) {
        return -1 * reverseSorting;
      }

      //If two scores in two rows are equivalent, sort by the Topic or the first column to break ties.
      return this.topicCompare(a, b, sorting);
    });
  }

  changeSorting = (column) => {
    const { columns, items } = this.props;
    const selectedColumn = columns[column].value;
    let sortedItems = [];
    const currentSorting = this.state.sorting.filter(
      (s) => s.column === selectedColumn,
    )[0];

    let newSorting = 'Ascending';
    if (this.state.selectedColumn === selectedColumn) {
      newSorting =
        currentSorting.direction === 'Ascending' ? 'Descending' : 'Ascending';
    }

    if (selectedColumn.startsWith('Part') || selectedColumn === 'Class Avg') {
      sortedItems = this.sortByScore(items, column, newSorting);
      this.updateColumn(selectedColumn, sortedItems, column, newSorting);
    } else {
      if (selectedColumn.startsWith('Topic')) {
        sortedItems = this.sortByTopic(items);
      } else {
        sortedItems = this.sortByAlpha(items, selectedColumn);
      }
      if (newSorting === 'Descending') {
        sortedItems.reverse();
      }

      this.updateColumn(selectedColumn, sortedItems, column, newSorting);
    }
  };

  iconFor = (column) => {
    const { direction } = this.state.sorting[column];
    return ResourcesTable.Sorting.get(direction);
  };

  updateColumn(selectedColumn, sortedItems, column, sorting) {
    const tableAriaLabel = `Resources Table. Sorted by ${selectedColumn}, ${sorting}`;
    this.setState((prevState) => ({
      ...prevState,
      selectedColumn,
      tableAriaLabel,
      items: sortedItems,
      sorting: prevState.sorting.map((col, index) => {
        if (index === column) {
          return { ...col, direction: sorting };
        }
        return { ...col, direction: 'Disabled' };
      }),
    }));
  }

  render() {
    const { columns, isStudent, studentContext } = this.props;
    const { hasContext } = studentContext;
    const { items, tableAriaLabel, selectedColumn, sorting } = this.state;

    return (
      <Table
        className={cn('Assignment--resources--table', {
          'has-student-layout': isStudent,
        })}
        data-test-resources-table
        aria-labelledby="resourcesTableCaption"
      >
        <caption
          id="resourcesTableCaption"
          aria-label={tableAriaLabel}
          aria-live="polite"
        />
        <TableHead>
          <TableRow data-test-resources-table-columns>
            {columns.map(({ label, className, children }, index) => {
              const isCurrentFilter = columns[index].value === selectedColumn;
              const { direction } = sorting[index];
              const ariaSort = isCurrentFilter ? direction : 'none';
              return (
                <TableCell
                  className={cn(
                    className,
                    'Assignment--resources--table-th-container',
                  )}
                  key={`th-${index}`}
                  scope="col"
                  aria-sort={ariaSort}
                  role="columnheader"
                >
                  <div className="flex flex-col">
                    <button
                      aria-label={`${label}, sortable`}
                      className="border-none bg-none order-2"
                      onClick={() => this.changeSorting(index)}
                    >
                      <div className="part-label">
                        {label}
                        <span>
                          <Icon
                            className="Assignment--resources--table__icon"
                            name={this.iconFor(index)}
                          />
                        </span>
                      </div>
                    </button>
                    {children}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <ResourcesItems
          scoreDisplay={this.props.scoreDisplay}
          reviewModeFlag={this.props.reviewModeFlag}
          studentContext={studentContext}
          isStudent={isStudent || hasContext}
          columns={columns}
          items={[...items]}
        />
      </Table>
    );
  }
}

export default ResourcesTable;
